import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SplitAreaComponent, SplitComponent } from 'angular-split';
import { catchError, combineLatest, filter, map, of, switchMap, take } from 'rxjs';

import { DeployStatusOverride, PlanMetadataVisu } from '@gfe/core/models/plan.model';
import { hasSuperUserOrAbove } from '@gfe/core/models/user-role.constants';
import { DeployChannelCountService } from '@gfe/core/services/deploy-plan-config/deploy-channel-count.service';
import { ICONS } from '@gfe/core/services/icons/icons.service';
import { selectUser } from '@gfe/core/store/auth/auth.selectors';
import { selectDataspaceModel } from '@gfe/core/store/dataspace/dataspace.selectors';
import { selectDeployStatusOverrides } from '@gfe/core/store/plan/plan.selectors';
import { computeOverallDeployStatus } from '@gfe/core/utils/deploy-status.utils';
import { isNil } from '@gfe/core/utils/shared-utils';
import {
  TableAction,
  TableColumn,
  TableConfig
} from '@gfe/shared/components/table/table.model';
import {
  ToolboxComponent,
  ToolboxSection
} from '@gfe/shared/components/toolbox/toolbox.component';

import { PlanningSpaceMetadataModel } from '@core/models/planning-space/planning-space.model';
import {
  FetchPlansForOnePlanningSpaceResponse,
  PlanningSpaceService
} from '@core/services/planning-space/planning-space.service';

import { BadgeComponent } from '@shared/components/badge/badge.component';
import { KpiComparisonComponent } from '@shared/components/kpi-comparison/kpi-comparison.component';
import { KpiComparisonItem } from '@shared/components/kpi-comparison/kpi-comparison.model';
import { KpiComparisonService } from '@shared/components/kpi-comparison/kpi-comparison.service';
import { TableComponent } from '@shared/components/table/table.component';

import { DeletePlansFlowComponent } from '@features/components/plan/delete-plans-flow/delete-plans-flow.component';
import { DuplicatePlanFlowComponent } from '@features/components/plan/duplicate-plan-flow/duplicate-plan-flow.component';
import { RenamePlanFlowComponent } from '@features/components/plan/rename-plan-flow/rename-plan-flow.component';
import {
  GraphConfig,
  PlanNode,
  PlanningSpaceGraphVisuComponent
} from '@features/components/planning-space/planning-space-graph-visu/planning-space-graph-visu.component';

/**
 * `expectedChannels` is how many channels a complete deploy covers. The node reads as
 * deployed on the same terms as the table's badge — every channel out, none failed — and
 * not on the row's `isDeployed`, which only means a deploy was attempted.
 */
function transformToPlanNode(
  planMetadata: PlanMetadataVisu,
  expectedChannels: number
): PlanNode {
  return {
    id: planMetadata.id,
    visuId: planMetadata.visuId,
    parentId: planMetadata?.parentPlanId,
    recipeId: planMetadata?.recipe?.id,
    isDeployed:
      computeOverallDeployStatus(planMetadata.deployStatus ?? [], expectedChannels) ===
      'SUCCESS',
    creationDate: planMetadata.createdAt,
    // Prefer the backend-provided trigger (so duplicates render as 'Duplication');
    // fall back to the old heuristic if it's missing.
    creationTrigger:
      planMetadata.creationTrigger ??
      (isNil(planMetadata?.parentPlanId) ? 'Initial' : 'Optimize')
  };
}

@Component({
  selector: 'app-plans',
  imports: [
    ToolboxComponent,
    TableComponent,
    PlanningSpaceGraphVisuComponent,
    SplitAreaComponent,
    SplitComponent,
    DuplicatePlanFlowComponent,
    RenamePlanFlowComponent,
    DeletePlansFlowComponent,
    BadgeComponent,
    KpiComparisonComponent,
    TranslatePipe
  ],
  providers: [DeployChannelCountService],
  templateUrl: './planning-space.component.html',
  styles: ``
})
export class PlanningSpaceComponent {
  graphConfig: GraphConfig = {
    onNodeClick: (planId: string) => this.navigateToPlan(planId)
  };
  private readonly translate = inject(TranslateService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly planningSpaceService = inject(PlanningSpaceService);
  private readonly deployChannels = inject(DeployChannelCountService);
  protected readonly selectedPlanIds = signal<Set<string>>(new Set<string>());
  protected readonly onlySelected = signal(false);
  /**
   * What the single bottom area holds. One signal rather than a flag per panel, so "graph and
   * comparison at once" — which would halve both — cannot be expressed.
   */
  private readonly bottomPanel = signal<'graph' | 'compare' | 'none'>('graph');
  /** What to restore when the comparison closes, so it does not force the graph back open. */
  private readonly panelBeforeCompare = signal<'graph' | 'none'>('graph');
  protected readonly isGraphEnabled = computed(() => this.bottomPanel() === 'graph');
  protected readonly isCompareEnabled = computed(() => this.bottomPanel() === 'compare');
  protected readonly canCompare = computed(() => this.selectedPlanIds().size >= 2);
  private readonly kpiComparison = inject(KpiComparisonService);
  protected readonly kpiDecimals = this.kpiComparison.decimals;
  protected readonly kpiOrder = this.kpiComparison.order;
  private readonly store = inject(Store);
  private dataspace = toSignal(
    this.store.select(selectDataspaceModel).pipe(filter((x) => !isNil(x)))
  );
  private dataSpaceId?: number;
  protected readonly ICONS = ICONS;
  protected actions = signal<TableAction<PlanMetadataVisu>[]>([
    {
      label: this.translate.instant('common.view'),
      icon: ICONS.arrowSquareOut,
      color: 'primary',
      action: (row: PlanMetadataVisu): void => this.navigateToPlan(row.id)
    }
  ]);
  protected columns = signal<TableColumn<PlanMetadataVisu>[]>([]);

  protected planMetadatasResponse = computed<FetchPlansForOnePlanningSpaceResponse>(() => {
    return this.plansForOnePlanningSpace();
  });

  protected planMetadatasArray = computed<PlanMetadataVisu[]>(() => {
    const response = this.planMetadatasResponse();
    const overrides = this.deployOverrides();
    if (!response.result || !Array.isArray(response.result)) {
      return [];
    }

    return [...response.result]
      .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      .map((plan, index) => {
        const base = plan.deployStatus ?? [];
        // Apply an override only when it is newer than the fetched status
        // (staleness guard) — a fresher fetch must not be overwritten by an older
        // websocket event.
        const deployStatus = base.map((ds) => {
          const key = `${plan.id}__${ds.configId}`;
          const ov = overrides[key];
          // Caveat: ov.updatedAt is the event time (server-provided where available;
          // see websocket handler / Task 11). Comparing it against the server's
          // ds.updatedAt assumes roughly-synced clocks.
          const fetchedAt = ds.updatedAt ? new Date(ds.updatedAt).getTime() : 0;
          return ov && ov.updatedAt > fetchedAt ? { ...ds, status: ov.status } : ds;
        });
        Object.entries(overrides)
          .filter(([key]) => key.startsWith(`${plan.id}__`))
          .forEach(([key, ov]) => {
            const configId = key.split('__')[1];
            if (!base.find((ds) => ds.configId === configId)) {
              // Newly-deployed config not yet in the fetched plan. `name` falls back
              // to configId until the next refetch; timestamps come from the event
              // (not fabricated with new Date()).
              const eventDate = new Date(ov.updatedAt);
              deployStatus.push({
                configId,
                name: configId,
                status: ov.status,
                error: null,
                warnings: [],
                createdAt: eventDate,
                updatedAt: eventDate
              });
            }
          });

        // `isDeployed` here means "a deploy was attempted" — a plan whose channels are
        // all ERROR/PROCESSING still counts. That is the right reading for the delete
        // confirmation, which warns about anything that may have reached a recipient; what
        // the deploy badge and the graph node report is completion, and they compute it
        // from the statuses instead.
        const isDeployed = plan.isDeployed || deployStatus.length > 0;

        return {
          ...plan,
          visuId: (index + 1).toString(),
          isDeployed,
          // Deployment is reported by the Deploy Status column, not by this one.
          status: {
            code: 'ok'
          },
          deployStatus
        };
      });
  });

  graphPlanNodes = computed<PlanNode[]>(() => {
    const plans = this.planMetadatasArray();
    const expectedChannels = this.deployChannels.enabledCount();
    return plans.map((plan) => transformToPlanNode(plan, expectedChannels));
  });

  /**
   * Derived from the rows currently rendered: the table matches the selection by object
   * reference, so it has to hold the very objects it is displaying to survive a refetch.
   */
  protected readonly selectedPlans = computed(
    () =>
      new Set(
        this.planMetadatasArray().filter((plan) => this.selectedPlanIds().has(plan.id))
      )
  );

  protected readonly hasSelection = computed(() => this.selectedPlanIds().size > 0);

  /**
   * Columns follow the table's own order rather than the order the user ticked the rows, so
   * they do not reshuffle underneath a comparison that is being read.
   */
  protected readonly compareItems = computed<KpiComparisonItem[]>(() =>
    this.kpiComparison.buildItems(
      this.planMetadatasArray()
        .filter((plan) => this.selectedPlanIds().has(plan.id))
        .map((plan) => ({
          id: plan.id,
          label: plan.name,
          sublabel: isNil(plan.visuId) ? undefined : `#${plan.visuId}`,
          rawKpis: plan.kpis
        }))
    )
  );

  /** The graph always shows every plan; only the table narrows to the selection. */
  protected readonly tableRows = computed(() =>
    this.onlySelected()
      ? this.planMetadatasArray().filter((plan) => this.selectedPlanIds().has(plan.id))
      : this.planMetadatasArray()
  );

  get tableConfig(): TableConfig<PlanMetadataVisu> {
    return {
      rowId: 'id',
      bordered: true,
      doubleClickAction: (row: PlanMetadataVisu) => this.navigateToPlan(row.id),
      rowContextMenuOptions: (row: PlanMetadataVisu) => [
        {
          label: this.translate.instant('common.duplicate'),
          icon: ICONS.copy,
          action: () => this.openDuplicate(row)
        },
        {
          label: this.translate.instant('common.rename'),
          icon: ICONS.pencil,
          action: () => this.openRename(row)
        },
        {
          label: this.translate.instant('common.delete'),
          icon: ICONS.trash,
          isDanger: true,
          action: () => this.openDelete([row.id])
        }
      ],
      selection: {
        enabled: true,
        highlightSelected: true,
        multiple: true,
        mode: 'checkbox',
        action: (row: PlanMetadataVisu) => this.toggleSelection(row.id)
      }
    };
  }

  // Bumped to re-fetch the plan list, so the table and the graph both pick up a new
  // duplicate or a new name, or drop deleted plans.
  private readonly refreshTrigger = signal(0);

  protected plansForOnePlanningSpace = toSignal(
    combineLatest([
      this.activatedRoute.paramMap.pipe(filter((x) => !isNil(x))),
      toObservable(this.refreshTrigger)
    ]).pipe(
      switchMap(([params]) => {
        const planningSpaceId = params.get('planningSpaceId') as string;

        return this.planningSpaceService.fetchAllPLanForOnePlanningSpace$({
          planningSpaceId
        });
      })
    ),
    {
      initialValue: {
        total: 0,
        page: 1,
        take: 25,
        result: []
      }
    }
  );

  /**
   * The space itself, not its plans: the visibility marker and whether it may be set live
   * here, and the toolbox reads both. Shares `refreshTrigger` so a flip refetches it.
   */
  protected readonly planningSpace = toSignal(
    combineLatest([
      this.activatedRoute.paramMap.pipe(filter((x) => !isNil(x))),
      toObservable(this.refreshTrigger)
    ]).pipe(
      switchMap(([params]) =>
        this.planningSpaceService
          .fetchOne$({
            planningSpaceId: params.get('planningSpaceId') as string
          })
          .pipe(
            // `toSignal` stores a stream error and replays it on every read, and this
            // signal is read from the template — so an unrecovered rejection throws on
            // every change detection pass, taking down the table and the graph along with
            // the marker. Recovering inside the switchMap ends the failed request alone:
            // catching on the outer stream would complete it, leaving every later refresh
            // unfetched.
            catchError((error: unknown) => {
              console.error('Error fetching the planning space:', error);
              return of<PlanningSpaceMetadataModel | null>(null);
            })
          )
      )
    ),
    { initialValue: null as PlanningSpaceMetadataModel | null }
  );

  private readonly currentUser = this.store.selectSignal(selectUser);

  protected readonly isInternal = computed(() => this.planningSpace()?.isInternal === true);

  /** Offered only to the roles the endpoint accepts, so the UI never invites a 403. */
  private readonly canChangeVisibility = computed(() =>
    hasSuperUserOrAbove(this.currentUser()?.role)
  );

  protected readonly canMakeVisible = computed(
    () => this.canChangeVisibility() && this.isInternal()
  );

  /**
   * Never re-derived here: `canBeMadeInternal` is the server's answer, and a second copy of
   * the creator-role and deploy conditions would drift from it.
   */
  protected readonly canMakeInternal = computed(
    () =>
      this.canChangeVisibility() &&
      !this.isInternal() &&
      this.planningSpace()?.canBeMadeInternal === true
  );

  private setInternal(isInternal: boolean): void {
    const planningSpaceId = this.planningSpace()?.id;
    if (isNil(planningSpaceId)) {
      return;
    }
    this.planningSpaceService
      .setPlanningSpaceInternal$(planningSpaceId, isInternal)
      .pipe(take(1))
      .subscribe(() => this.refreshTrigger.update((v) => v + 1));
  }

  // Duplicate flow state (shared by table row menu and graph node menu).
  protected readonly duplicateFlowOpen = signal(false);
  protected readonly duplicateTarget = signal<PlanMetadataVisu | null>(null);

  // Rename flow state (shared by table row menu and graph node menu).
  protected readonly renameFlowOpen = signal(false);
  protected readonly renameTarget = signal<PlanMetadataVisu | null>(null);

  // Delete flow state (shared by the bulk action, the table row menu and the graph node
  // menu). The ids are frozen when the flow opens: changing the selection behind an open
  // confirmation must not change what gets deleted.
  protected readonly deleteFlowOpen = signal(false);
  protected readonly deleteTargetIds = signal<string[]>([]);

  private deployOverrides = toSignal(this.store.select(selectDeployStatusOverrides), {
    initialValue: {} as Record<string, DeployStatusOverride>
  });
  private readonly router = inject(Router);
  protected currentLang = toSignal(
    this.translate.onLangChange.pipe(
      filter((value) => !isNil(value)),
      map((value) => value.lang)
    ),
    { initialValue: this.translate.getCurrentLang() }
  );

  constructor() {
    effect(() => {
      this.currentLang();
      // Rebuilt when the channel count lands, or the deploy column would keep rendering
      // from the zero it started with.
      this.deployChannels.enabledCount();
      this.columns.set(this.buildTableColumns());
    });
    // An open comparison with nothing left to compare would keep half the viewport while the
    // user is reaching for the very rows it is covering.
    effect(() => {
      if (this.isCompareEnabled() && !this.canCompare()) {
        untracked(() => this.closeCompare());
      }
    });
    effect(() => {
      if (isNil(this.dataSpaceId)) {
        this.dataSpaceId = this.dataspace()?.id;
        return;
      }

      if (this.dataSpaceId !== this.dataspace()?.id) {
        setTimeout(() => {
          this.router.navigate(['ds', this.dataspace()!.id, 'planning-spaces']);
        });
      }
    });
  }

  buildTableColumns(): TableColumn<PlanMetadataVisu>[] {
    return [
      {
        key: 'visuId',
        label: '#',
        type: 'string',
        width: 50
      },
      {
        key: 'status',
        label: this.translate.instant('common.status'),
        type: 'planning-status',
        width: 100
      },
      {
        key: 'creationInfo' as keyof PlanMetadataVisu,
        label: this.translate.instant('features.planning_space.parent'),
        type: 'string',
        width: 80,
        renderHook: (_value, row) => {
          let result = '-';
          this.planMetadatasArray().forEach((plan) => {
            if (plan.id === row?.parentPlanId) {
              result = plan.visuId ?? '-';
            }
          });
          return result;
        }
      },
      {
        key: 'name',
        label: this.translate.instant('common.name'),
        type: 'string'
      },
      {
        key: 'description',
        label: this.translate.instant('common.description'),
        type: 'string'
      },
      {
        key: 'createdAt',
        label: this.translate.instant('common.created'),
        type: 'datetime',
        width: 150
      },
      {
        key: 'deployStatus',
        label: this.translate.instant('features.plan.deploy.status_detail'),
        type: 'deploy-status-overall',
        width: 110,
        renderHook: (_value: unknown, row?: PlanMetadataVisu) => ({
          statuses: row?.deployStatus ?? [],
          expectedChannels: this.deployChannels.enabledCount()
        })
      }
    ];
  }

  // A computed, not a signal fed by the language effect: the delete button's `disabled`
  // flag has to follow the selection, which changes far more often than the language.
  protected readonly toolboxSections = computed<ToolboxSection[]>(() => {
    this.currentLang();
    return this.buildToolboxSections();
  });

  buildToolboxSections(): ToolboxSection[] {
    const graphVisuButtonIcon = this.isGraphEnabled() ? ICONS.eyeSlash : ICONS.eye;
    const graphVisuButtonTooltipTranslationKey = this.isGraphEnabled()
      ? 'features.data.toolbox.disable_visu'
      : 'features.data.toolbox.enable_visu';
    const graphVisuButtonTooltip = this.translate.instant(
      graphVisuButtonTooltipTranslationKey
    );

    return [
      {
        id: 'actions',
        items: [
          {
            id: 'visu',
            icon: graphVisuButtonIcon,
            tooltip: graphVisuButtonTooltip,
            callback: () => {
              this.bottomPanel.update((panel) => (panel === 'graph' ? 'none' : 'graph'));
            }
          },
          {
            id: 'compare-kpis',
            icon: ICONS.scales,
            active: this.isCompareEnabled(),
            disabled: !this.canCompare(),
            tooltip: this.translate.instant(
              this.canCompare()
                ? 'features.planning_space.compare.tooltip'
                : 'features.planning_space.compare.disabled_tooltip'
            ),
            callback: () => {
              if (this.isCompareEnabled()) this.closeCompare();
              else this.openCompare();
            }
          },
          /*
           * Exactly one of the two ever shows, and only when the endpoint would accept it:
           * a space that is public but ineligible offers neither, because the reason sits in
           * server state the operator cannot act on from this page. The icon names the
           * action, like the graph toggle beside it.
           */
          ...(this.canMakeVisible()
            ? [
                {
                  id: 'make-visible',
                  icon: ICONS.detectiveSlash,
                  tooltip: this.translate.instant(
                    'features.planning_space.internal.make_visible_tooltip'
                  ),
                  callback: (): void => this.setInternal(false)
                }
              ]
            : []),
          ...(this.canMakeInternal()
            ? [
                {
                  id: 'make-internal',
                  icon: ICONS.detective,
                  tooltip: this.translate.instant(
                    'features.planning_space.internal.make_internal_tooltip'
                  ),
                  callback: (): void => this.setInternal(true)
                }
              ]
            : [])
        ]
      },
      {
        id: 'bulk-actions',
        items: [
          {
            id: 'delete',
            icon: ICONS.trash,
            tooltip: this.translate.instant('common.delete'),
            disabled: !this.hasSelection(),
            callback: () => this.openDelete(Array.from(this.selectedPlanIds()))
          }
        ]
      }
    ];
  }

  protected openCompare(): void {
    const current = this.bottomPanel();
    if (current !== 'compare') this.panelBeforeCompare.set(current);
    this.bottomPanel.set('compare');
  }

  protected closeCompare(): void {
    this.bottomPanel.set(this.panelBeforeCompare());
  }

  protected openDelete(planIds: string[]): void {
    if (planIds.length === 0) {
      return;
    }
    this.deleteTargetIds.set(planIds);
    this.deleteFlowOpen.set(true);
  }

  protected onPlansDeleteCompleted(): void {
    // Refetch on success *and* failure: a refused batch may already have deleted the
    // plans it processed before refusing, so the list on screen cannot be trusted and
    // the selection must not be restored optimistically.
    this.clearSelection();
    this.refreshTrigger.update((v) => v + 1);
  }

  protected clearSelection(): void {
    this.selectedPlanIds.set(new Set<string>());
    this.onlySelected.set(false);
  }

  protected toggleSelection(planId: string): void {
    this.selectedPlanIds.update((ids) => {
      const next = new Set(ids);
      if (!next.delete(planId)) {
        next.add(planId);
      }
      return next;
    });
  }

  onPlanMetadataDelete($event: string): void {
    this.openDelete([$event]);
  }

  onPlanNodeClick($event: string): void {
    this.toggleSelection($event);
  }

  private navigateToPlan(planId: string): void {
    this.router.navigate(['plan', planId], { relativeTo: this.activatedRoute });
  }

  protected openDuplicate(plan: PlanMetadataVisu): void {
    this.duplicateTarget.set(plan);
    this.duplicateFlowOpen.set(true);
  }

  protected openDuplicateById(planId: string): void {
    const plan = this.planMetadatasArray().find((p) => p.id === planId);
    if (isNil(plan)) {
      console.error('Plan not found for id:', planId);
      return;
    }
    this.openDuplicate(plan);
  }

  protected onPlanDuplicated(): void {
    // Re-fetch the list so the new copy appears (the graph re-renders from it).
    this.refreshTrigger.update((v) => v + 1);
  }

  protected openRename(plan: PlanMetadataVisu): void {
    this.renameTarget.set(plan);
    this.renameFlowOpen.set(true);
  }

  protected openRenameById(planId: string): void {
    const plan = this.planMetadatasArray().find((p) => p.id === planId);
    if (isNil(plan)) {
      console.error('Plan not found for id:', planId);
      return;
    }
    this.openRename(plan);
  }

  protected onPlanRenamed(): void {
    this.refreshTrigger.update((v) => v + 1);
  }
}
